import React, { useState, useEffect, useCallback } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell, AreaChart, Area,
    PieChart, Pie, ComposedChart, Line,
} from 'recharts';
import NavigationBar from '../components/layout/Navbar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');
const getRole  = () => localStorage.getItem('role')  || sessionStorage.getItem('role');

const fmt  = (n, dec=1) => (n==null||n==='') ? '—' : Number(n).toLocaleString('es-CO',{maximumFractionDigits:dec});
const fmtK = n => {
    if (n==null) return '—';
    return fmt(n,0);
};

/* ── Paleta ─────────────────────────────────────────────────────────────── */
const P = {
    bg:      '#070B14',
    sidebar: '#0C1220',
    card:    '#0F1623',
    cardAlt: '#111927',
    border:  '#1E2D45',
    hover:   '#162035',
    text:    '#E2EAF4',
    dim:     '#5A7399',
    cyan:    '#00C9FF',
    green:   '#00E5A0',
    coral:   '#FF5E7D',
    blue:    '#4D8EFF',
    amber:   '#FFB020',
    purple:  '#9B6DFF',
    teal:    '#00BFA5',
};

const FC = [P.cyan, P.green, P.amber, P.purple, P.coral];
const AC = [P.cyan, P.coral, P.blue, P.amber, P.green, P.purple, P.teal, '#FF8A65'];
const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

/* ── Tooltip ────────────────────────────────────────────────────────────── */
const TT = ({ active, payload, label, sfx='' }) => {
    if (!active||!payload?.length) return null;
    return (
        <div style={{background:'#162035',border:`1px solid ${P.border}`,borderRadius:10,padding:'10px 14px',fontSize:12,boxShadow:'0 12px 32px rgba(0,0,0,.6)'}}>
            <div style={{fontWeight:700,marginBottom:6,color:P.text}}>{label}</div>
            {payload.map((p,i)=>(
                <div key={i} style={{color:p.color||P.cyan,marginBottom:2}}>
                    {p.name}: <b>{fmt(p.value)}{sfx}</b>
                </div>
            ))}
        </div>
    );
};

/* ── Pill ───────────────────────────────────────────────────────────────── */
const Pill = ({label,active,color=P.cyan,onClick}) => (
    <button onClick={onClick} style={{
        padding:'4px 12px',borderRadius:6,fontSize:11,cursor:'pointer',
        border:`1px solid ${active?color:P.border}`,
        background:active?`${color}18`:'transparent',
        color:active?color:P.dim,fontWeight:active?700:400,transition:'.15s',
        whiteSpace:'nowrap',
    }}>{label}</button>
);

/* ── KPI strip card ─────────────────────────────────────────────────────── */
const Kpi = ({label,value,unit='',color=P.cyan,sub}) => (
    <div style={{
        background:P.card,border:`1px solid ${P.border}`,borderRadius:12,
        padding:'20px 22px',flex:1,minWidth:160,position:'relative',overflow:'hidden',
        borderTop:`3px solid ${color}`,
    }}>
        <div style={{fontSize:10,color:P.dim,fontWeight:700,letterSpacing:'.08em',marginBottom:10}}>{label}</div>
        <div style={{display:'flex',alignItems:'baseline',gap:5}}>
            <span style={{fontSize:36,fontWeight:900,color,lineHeight:1,letterSpacing:'-.02em'}}>{value}</span>
            {unit&&<span style={{fontSize:14,color:P.dim,fontWeight:500}}>{unit}</span>}
        </div>
        {sub&&<div style={{marginTop:10,fontSize:11,color:P.dim,borderTop:`1px solid ${P.border}`,paddingTop:8}}>{sub}</div>}
        <div style={{position:'absolute',top:-30,right:-20,width:80,height:80,borderRadius:'50%',background:color,opacity:.06,filter:'blur(18px)',pointerEvents:'none'}}/>
    </div>
);

/* ── Panel ──────────────────────────────────────────────────────────────── */
const Panel = ({children,title,sub,style,noPad}) => (
    <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:12,overflow:'hidden',...style}}>
        {title&&(
            <div style={{padding:'14px 18px',borderBottom:`1px solid ${P.border}`,background:P.cardAlt}}>
                <div style={{fontSize:11,fontWeight:700,color:P.dim,letterSpacing:'.08em',textTransform:'uppercase'}}>{title}</div>
                {sub&&<div style={{fontSize:10,color:P.dim,opacity:.6,marginTop:2}}>{sub}</div>}
            </div>
        )}
        <div style={noPad?{}:{padding:'16px 18px'}}>{children}</div>
    </div>
);

/* ── Sidebar stat row ───────────────────────────────────────────────────── */
const SideRow = ({label,value,color=P.cyan,pct}) => (
    <div style={{marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
            <span style={{fontSize:11,color:P.dim}}>{label}</span>
            <span style={{fontSize:11,fontWeight:700,color}}>{value}</span>
        </div>
        {pct!=null&&(
            <div style={{height:3,background:P.border,borderRadius:2}}>
                <div style={{height:3,borderRadius:2,background:color,width:`${Math.min(pct,100)}%`,transition:'.4s'}}/>
            </div>
        )}
    </div>
);

/* ══════════════════════════════════════════════════════════════════════════
   DASHBOARD
══════════════════════════════════════════════════════════════════════════ */
const Dashboard = () => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);
    const [idCp,    setIdCp]    = useState('');
    const [anio,    setAnio]    = useState(new Date().getFullYear());

    const isAdmin = getRole() === 'Admin';

    const load = useCallback(async (cp,yr) => {
        setLoading(true); setError(null);
        try {
            const p = new URLSearchParams({anio:yr});
            if (cp) p.set('id_cp',cp);
            const r = await fetch(`${API_URL}/dashboard?${p}`,{headers:{Authorization:`Bearer ${getToken()}`}});
            if (!r.ok) throw new Error(`Error ${r.status}`);
            setData(await r.json());
        } catch(e){setError(e.message);}
        finally{setLoading(false);}
    },[]);

    useEffect(()=>{load(idCp,anio);},[idCp,anio,load]);

    if (error) return (
        <div style={{background:P.bg,minHeight:'100vh'}}>
            <NavigationBar/>
            <div style={{textAlign:'center',marginTop:80,color:P.coral}}>Error: {error}</div>
        </div>
    );

    const {kpis,kpisMes,porMes,topActividades,actividadesPorMes,
           comparativaFincas,porAnio,fincas,aniosDisponibles} = data||{};

    const fincaNombre = fincas?.find(f=>String(f.id)===idCp)?.nombre||'Todas las fincas';

    // Trazabilidad
    const actsU = [...new Set((actividadesPorMes||[]).map(r=>r.actividad))].slice(0,10);
    const traz  = actsU.map(act=>{
        const row={actividad:act};
        (actividadesPorMes||[]).filter(r=>r.actividad===act).forEach(r=>{row[`m${r.mes_num}`]=r.horas;});
        return row;
    });

    // Pie
    const pieData = (topActividades||[]).slice(0,5).map((a,i)=>({name:a.actividad,value:Number(a.horas)||0,color:AC[i]}));
    const pieTotal = pieData.reduce((s,d)=>s+d.value,0);

    // Sidebar stats (comparativa fincas o totales)
    const sideStats = comparativaFincas?.length
        ? comparativaFincas.map((f,i)=>({label:f.finca,value:fmtK(f.horas)+' h',color:FC[i%FC.length],pct:f.horas/(Math.max(...comparativaFincas.map(x=>x.horas))||1)*100}))
        : [];

    const maxHorasMes = Math.max(...(porMes||[]).map(m=>m.horas)||[1],1);

    const mesActual = new Date().getMonth()+1;

    return (
        <div style={{background:P.bg,minHeight:'100vh',color:P.text}}>
            <NavigationBar/>

            {/* ── Layout principal ── */}
            <div style={{display:'flex',minHeight:'calc(100vh - 56px)'}}>

                {/* ════ SIDEBAR ════ */}
                <aside style={{
                    width:240,flexShrink:0,background:P.sidebar,
                    borderRight:`1px solid ${P.border}`,
                    display:'flex',flexDirection:'column',
                    padding:'24px 16px',gap:0,
                    position:'sticky',top:56,height:'calc(100vh - 56px)',overflowY:'auto',
                }}>
                    {/* Título */}
                    <div style={{marginBottom:28}}>
                        <div style={{fontSize:13,fontWeight:800,color:P.text,letterSpacing:'-.01em'}}>Dashboard Gerencial</div>
                        <div style={{fontSize:11,color:P.dim,marginTop:3}}>{fincaNombre} · {anio}</div>
                    </div>

                    {/* Año */}
                    <div style={{marginBottom:20}}>
                        <div style={{fontSize:10,color:P.dim,fontWeight:700,letterSpacing:'.08em',marginBottom:8}}>AÑO</div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                            {(aniosDisponibles||[]).map(a=><Pill key={a} label={a} active={anio===a} color={P.blue} onClick={()=>setAnio(a)}/>)}
                        </div>
                    </div>

                    {/* Finca */}
                    {isAdmin&&fincas&&(
                        <div style={{marginBottom:24}}>
                            <div style={{fontSize:10,color:P.dim,fontWeight:700,letterSpacing:'.08em',marginBottom:8}}>FINCA</div>
                            <div style={{display:'flex',flexDirection:'column',gap:4}}>
                                <button onClick={()=>setIdCp('')} style={{
                                    padding:'7px 10px',borderRadius:7,fontSize:11,cursor:'pointer',textAlign:'left',
                                    border:`1px solid ${idCp===''?P.cyan:P.border}`,
                                    background:idCp===''?`${P.cyan}14`:'transparent',
                                    color:idCp===''?P.cyan:P.dim,fontWeight:idCp===''?700:400,
                                }}>Todas las fincas</button>
                                {fincas.map((f,i)=>(
                                    <button key={f.id} onClick={()=>setIdCp(String(f.id))} style={{
                                        padding:'7px 10px',borderRadius:7,fontSize:11,cursor:'pointer',textAlign:'left',
                                        border:`1px solid ${idCp===String(f.id)?FC[i%FC.length]:P.border}`,
                                        background:idCp===String(f.id)?`${FC[i%FC.length]}14`:'transparent',
                                        color:idCp===String(f.id)?FC[i%FC.length]:P.dim,
                                        fontWeight:idCp===String(f.id)?700:400,
                                        display:'flex',alignItems:'center',gap:7,
                                    }}>
                                        <span style={{width:7,height:7,borderRadius:'50%',background:FC[i%FC.length],flexShrink:0}}/>
                                        {f.nombre}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Divider */}
                    <div style={{height:1,background:P.border,marginBottom:20}}/>

                    {/* Stats rápidos */}
                    {!loading&&(
                        <>
                            <div style={{fontSize:10,color:P.dim,fontWeight:700,letterSpacing:'.08em',marginBottom:14}}>RESUMEN {anio}</div>
                            <SideRow label="Registros"    value={fmtK(kpis?.total_registros)}      color={P.cyan}   pct={100}/>
                            <SideRow label="Horas labor"  value={fmtK(kpis?.horas_trabajadas)+' h'} color={P.amber}  pct={null}/>
                            <SideRow label="Litros"       value={fmtK(kpis?.litros_aplicados)+' L'} color={P.green}  pct={null}/>
                            <SideRow label="Jornales"     value={fmt(kpis?.horas_trabajadas/8.8,0)} color={P.purple} pct={null}/>

                            {sideStats.length>0&&(
                                <>
                                    <div style={{height:1,background:P.border,margin:'12px 0 16px'}}/>
                                    <div style={{fontSize:10,color:P.dim,fontWeight:700,letterSpacing:'.08em',marginBottom:14}}>FINCAS — HORAS</div>
                                    {sideStats.map((s,i)=><SideRow key={i} {...s}/>)}
                                </>
                            )}
                        </>
                    )}
                </aside>

                {/* ════ CONTENIDO ════ */}
                <main style={{flex:1,padding:'24px 28px 80px',overflowX:'hidden'}}>

                    {loading?(
                        <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',color:P.dim}}>
                            <div style={{textAlign:'center'}}>
                                <div style={{fontSize:32,marginBottom:12,opacity:.3}}>◌</div>
                                <div style={{fontSize:14}}>Cargando datos...</div>
                            </div>
                        </div>
                    ):(
                        <>
                            {/* ── KPIs ── */}
                            <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'}}>
                                <Kpi label="REGISTROS AÑO"      value={fmtK(kpis?.total_registros)}       color={P.cyan}   sub={`Mes actual: ${fmt(kpisMes?.total_registros,0)}`}/>
                                <Kpi label="HORAS LABOR"        value={fmtK(kpis?.horas_trabajadas)}      unit="h" color={P.amber}  sub={`Mes: ${fmt(kpisMes?.horas_trabajadas)} h`}/>
                                <Kpi label="LLUVIA ACUMULADA"   value={fmt(kpis?.horas_lluvia,0)}         unit="h" color={P.blue}   sub={`${kpis?.meses_con_registro} meses con datos`}/>
                                <Kpi label="LITROS APLICADOS"   value={fmtK(kpis?.litros_aplicados)}      unit="L" color={P.green}  sub={`Mes: ${fmtK(kpisMes?.litros_aplicados)} L`}/>
                                <Kpi label="JORNALES"           value={fmt(kpis?.horas_trabajadas/8.8,0)} color={P.purple} sub="Horas ÷ 8.8"/>
                            </div>

                            {/* ── Evolución + Pie ── */}
                            <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:14,marginBottom:14}}>
                                <Panel title="EVOLUCIÓN MENSUAL" sub={`Horas de labor vs lluvia · ${anio}`}>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <ComposedChart data={porMes} margin={{top:10,right:20,left:0,bottom:0}}>
                                            <defs>
                                                <linearGradient id="gHoras" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={P.amber} stopOpacity={.5}/>
                                                    <stop offset="100%" stopColor={P.amber} stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke={P.border}/>
                                            <XAxis dataKey="mes" tick={{fontSize:10,fill:P.dim}} axisLine={false} tickLine={false}/>
                                            <YAxis yAxisId="l" tick={{fontSize:10,fill:P.dim}} axisLine={false} tickLine={false}/>
                                            <YAxis yAxisId="r" orientation="right" tick={{fontSize:10,fill:P.dim}} axisLine={false} tickLine={false}/>
                                            <Tooltip content={<TT/>} cursor={{fill:'#ffffff04'}}/>
                                            <Bar yAxisId="l" dataKey="horas" name="Horas labor" fill={P.amber} radius={[4,4,0,0]} opacity={.9}
                                                label={{position:'top',fontSize:9,fill:P.dim,formatter:v=>v>0?fmtK(v):''}}/>
                                            <Line yAxisId="r" type="monotone" dataKey="lluvia" name="Lluvia h" stroke={P.blue} strokeWidth={2.5} dot={{r:3,fill:P.blue,strokeWidth:0}}/>
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </Panel>

                                <Panel title="TOP ACTIVIDADES" sub="Distribución de horas">
                                    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:16}}>
                                        <PieChart width={130} height={130}>
                                            <Pie data={pieData} cx={60} cy={60} innerRadius={36} outerRadius={60} dataKey="value" strokeWidth={0}>
                                                {pieData.map((d,i)=><Cell key={i} fill={d.color}/>)}
                                            </Pie>
                                            <Tooltip content={<TT sfx=" h"/>}/>
                                        </PieChart>
                                        <div style={{width:'100%',display:'flex',flexDirection:'column',gap:8}}>
                                            {pieData.map((d,i)=>(
                                                <div key={i} style={{display:'flex',alignItems:'center',gap:8}}>
                                                    <span style={{width:8,height:8,borderRadius:2,background:d.color,flexShrink:0}}/>
                                                    <span style={{fontSize:10,color:P.dim,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.name}</span>
                                                    <span style={{fontSize:11,fontWeight:700,color:d.color}}>{pieTotal>0?Math.round(d.value/pieTotal*100):0}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Panel>
                            </div>

                            {/* ── 3 gráficos medianos ── */}
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14,marginBottom:14}}>
                                <Panel title="LITROS APLICADOS / MES" sub={anio}>
                                    <ResponsiveContainer width="100%" height={160}>
                                        <AreaChart data={porMes} margin={{top:10,right:5,left:0,bottom:0}}>
                                            <defs>
                                                <linearGradient id="gL" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={P.green} stopOpacity={.4}/>
                                                    <stop offset="100%" stopColor={P.green} stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke={P.border}/>
                                            <XAxis dataKey="mes" tick={{fontSize:9,fill:P.dim}} axisLine={false} tickLine={false}/>
                                            <YAxis tick={{fontSize:9,fill:P.dim}} axisLine={false} tickLine={false}/>
                                            <Tooltip content={<TT sfx=" L"/>} cursor={{stroke:P.green,strokeWidth:1}}/>
                                            <Area type="monotone" dataKey="litros" name="Litros" stroke={P.green} strokeWidth={2.5} fill="url(#gL)" dot={{r:3,fill:P.green,strokeWidth:0}}/>
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </Panel>

                                <Panel title="JORNALES / MES" sub={anio}>
                                    <ResponsiveContainer width="100%" height={160}>
                                        <BarChart data={porMes} margin={{top:5,right:5,left:0,bottom:0}}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={P.border}/>
                                            <XAxis dataKey="mes" tick={{fontSize:9,fill:P.dim}} axisLine={false} tickLine={false}/>
                                            <YAxis tick={{fontSize:9,fill:P.dim}} axisLine={false} tickLine={false}/>
                                            <Tooltip content={<TT/>} cursor={{fill:'#ffffff04'}}/>
                                            <Bar dataKey="jornales" name="Jornales" radius={[3,3,0,0]}>
                                                {(porMes||[]).map((_,i)=>(
                                                    <Cell key={`jornales-mes-${i}`} fill={i+1===mesActual&&anio===new Date().getFullYear()?P.cyan:P.coral} opacity={.85}/>
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Panel>
                            </div>

                            {/* ── Top actividades + Comparativa ── */}
                            <div style={{display:'grid',gridTemplateColumns:isAdmin&&!idCp&&comparativaFincas?.length?'3fr 2fr':'1fr',gap:14,marginBottom:14}}>
                                <Panel title="TOP ACTIVIDADES" sub={`Horas totales · ${anio}`}>
                                    <ResponsiveContainer width="100%" height={280}>
                                        <BarChart data={topActividades} margin={{top:20,right:10,left:0,bottom:60}}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={P.border} vertical={false}/>
                                            <XAxis dataKey="actividad" tick={{fontSize:9,fill:P.dim}} axisLine={false} tickLine={false}
                                                angle={-35} textAnchor="end" interval={0}/>
                                            <YAxis tick={{fontSize:9,fill:P.dim}} axisLine={false} tickLine={false}/>
                                            <Tooltip content={<TT sfx=" h"/>} cursor={{fill:'#ffffff04'}}/>
                                            <Bar dataKey="horas" name="Horas" radius={[5,5,0,0]}
                                                label={{position:'top',fontSize:9,fill:P.dim,formatter:v=>fmt(v,0)+' h'}}>
                                                {topActividades?.map((a,i)=><Cell key={`act-${a.actividad}`} fill={AC[i%AC.length]}/>)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Panel>

                                {isAdmin&&!idCp&&comparativaFincas?.length>0&&(
                                    <Panel title="COMPARATIVA FINCAS" sub={`Año ${anio}`}>
                                        <ResponsiveContainer width="100%" height={180}>
                                            <BarChart data={comparativaFincas} margin={{top:5,right:10,left:0,bottom:0}}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={P.border}/>
                                                <XAxis dataKey="finca" tick={{fontSize:9,fill:P.dim}} axisLine={false} tickLine={false}/>
                                                <YAxis tick={{fontSize:9,fill:P.dim}} axisLine={false} tickLine={false}/>
                                                <Tooltip content={<TT sfx=" h"/>} cursor={{fill:'#ffffff04'}}/>
                                                <Bar dataKey="horas" name="Horas" radius={[5,5,0,0]}>
                                                    {comparativaFincas.map((f,i)=><Cell key={`cf-${f.id}`} fill={FC[i%FC.length]}/>)}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                        <div style={{marginTop:14,display:'flex',flexDirection:'column',gap:8}}>
                                            {comparativaFincas.map((f,i)=>(
                                                <div key={i} style={{display:'flex',alignItems:'center',gap:8}}>
                                                    <span style={{width:8,height:8,borderRadius:2,background:FC[i%FC.length],flexShrink:0}}/>
                                                    <span style={{fontSize:11,color:P.dim,flex:1}}>{f.finca}</span>
                                                    <span style={{fontSize:11,fontWeight:700,color:FC[i%FC.length]}}>{fmtK(f.horas)} h</span>
                                                    <span style={{fontSize:10,color:P.dim}}>{fmtK(f.litros)} L</span>
                                                </div>
                                            ))}
                                        </div>
                                    </Panel>
                                )}
                            </div>

                            {/* ── Heatmap trazabilidad ── */}
                            <Panel title="TRAZABILIDAD DE LABORES" sub={`Horas por actividad y mes · ${anio}`} noPad style={{marginBottom:14}}>
                                <div style={{overflowX:'auto'}}>
                                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                                        <thead>
                                            <tr style={{background:P.cardAlt}}>
                                                <th style={{padding:'10px 16px',textAlign:'left',fontWeight:700,color:P.dim,fontSize:10,borderBottom:`1px solid ${P.border}`,whiteSpace:'nowrap',minWidth:180,letterSpacing:'.05em'}}>ACTIVIDAD</th>
                                                {MESES.map((m,i)=>(
                                                    <th key={m} style={{
                                                        padding:'10px 6px',textAlign:'center',fontWeight:600,fontSize:10,
                                                        borderBottom:`1px solid ${P.border}`,whiteSpace:'nowrap',minWidth:46,
                                                        color:i+1===mesActual&&anio===new Date().getFullYear()?P.cyan:P.dim,
                                                        letterSpacing:'.04em',
                                                    }}>
                                                        {m}
                                                        {i+1===mesActual&&anio===new Date().getFullYear()&&<div style={{width:4,height:4,borderRadius:'50%',background:P.cyan,margin:'3px auto 0'}}/>}
                                                    </th>
                                                ))}
                                                <th style={{padding:'10px 16px',textAlign:'right',fontWeight:700,color:P.dim,fontSize:10,borderBottom:`1px solid ${P.border}`,letterSpacing:'.05em'}}>TOTAL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {traz.map((row,i)=>{
                                                const vals = Array.from({length:12},(_,j)=>Number(row[`m${j+1}`])||0);
                                                const total = vals.reduce((a,b)=>a+b,0);
                                                const maxV  = Math.max(...traz.flatMap(r=>Array.from({length:12},(_,j)=>Number(r[`m${j+1}`])||0)),1);
                                                const col   = AC[i%AC.length];
                                                return (
                                                    <tr key={i}
                                                        onMouseEnter={e=>e.currentTarget.style.background=P.hover}
                                                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                                        <td style={{padding:'9px 16px',fontWeight:600,color:P.text,fontSize:11,whiteSpace:'nowrap',borderBottom:`1px solid ${P.border}20`}}>
                                                            <span style={{display:'inline-block',width:8,height:8,borderRadius:2,background:col,marginRight:9}}/>
                                                            {row.actividad}
                                                        </td>
                                                        {vals.map((h,j)=>{
                                                            const alpha = h>0?Math.round((0.12+(h/maxV)*.75)*255).toString(16).padStart(2,'0'):'00';
                                                            return (
                                                                <td key={j} title={h>0?`${fmt(h,0)} h`:''} style={{
                                                                    padding:'8px 4px',textAlign:'center',
                                                                    background:h>0?`${col}${alpha}`:'transparent',
                                                                    color:h>0?P.text:P.border,
                                                                    fontWeight:h>0?700:400,fontSize:10,
                                                                    borderBottom:`1px solid ${P.border}20`,
                                                                    transition:'.2s',
                                                                }}>
                                                                    {h>0?fmtK(h):'·'}
                                                                </td>
                                                            );
                                                        })}
                                                        <td style={{padding:'8px 16px',textAlign:'right',fontWeight:800,color:col,fontSize:11,borderBottom:`1px solid ${P.border}20`}}>
                                                            {fmtK(total)} h
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </Panel>

                            {/* ── Histórico por año ── */}
                            <Panel title="HISTÓRICO POR AÑO" noPad>
                                <table style={{width:'100%',borderCollapse:'collapse'}}>
                                    <thead>
                                        <tr style={{background:P.cardAlt}}>
                                            {['AÑO','REGISTROS','OPERARIOS','JORNALES','HORAS LABOR','LLUVIA','LITROS'].map(h=>(
                                                <th key={h} style={{padding:'11px 16px',textAlign:'left',fontWeight:700,color:P.dim,fontSize:10,borderBottom:`1px solid ${P.border}`,letterSpacing:'.06em'}}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {porAnio?.map((row,i)=>(
                                            <tr key={i}
                                                onMouseEnter={e=>e.currentTarget.style.background=P.hover}
                                                onMouseLeave={e=>e.currentTarget.style.background=row.anio===anio?`${P.cyan}08`:'transparent'}
                                                style={{background:row.anio===anio?`${P.cyan}08`:'transparent',transition:'.1s'}}>
                                                <td style={{padding:'12px 16px',fontWeight:900,color:row.anio===anio?P.cyan:P.text,fontSize:14,borderBottom:`1px solid ${P.border}15`}}>
                                                    {row.anio}
                                                    {row.anio===anio&&<span style={{marginLeft:8,fontSize:9,background:`${P.cyan}20`,color:P.cyan,padding:'2px 7px',borderRadius:10,fontWeight:700}}>actual</span>}
                                                </td>
                                                <td style={{padding:'12px 16px',color:P.dim,fontSize:12,borderBottom:`1px solid ${P.border}15`}}>{fmt(row.registros,0)}</td>
                                                <td style={{padding:'12px 16px',color:P.dim,fontSize:12,borderBottom:`1px solid ${P.border}15`}}>{fmt(row.operarios,0)}</td>
                                                <td style={{padding:'12px 16px',color:P.text,fontSize:12,fontWeight:700,borderBottom:`1px solid ${P.border}15`}}>{fmt(row.jornales,1)}</td>
                                                <td style={{padding:'12px 16px',color:P.text,fontSize:12,fontWeight:700,borderBottom:`1px solid ${P.border}15`}}>{fmt(row.horas)} h</td>
                                                <td style={{padding:'12px 16px',color:P.blue,fontSize:12,fontWeight:700,borderBottom:`1px solid ${P.border}15`}}>{fmt(row.lluvia)} h</td>
                                                <td style={{padding:'12px 16px',color:P.green,fontSize:12,fontWeight:700,borderBottom:`1px solid ${P.border}15`}}>{fmtK(row.litros)} L</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Panel>

                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Dashboard;
